<script setup lang="ts">
interface Step {
  title: string;
  desc: string;
}

defineProps<{
  steps: Step[];
  modelValue: number;
  badge?: string;
  title: string;
  accentTitle: string;
}>();

defineEmits<{
  (e: 'update:modelValue', value: number): void;
  (e: 'step-click'): void;
}>();
</script>

<template>
  <div class="walkthrough-content animate-on-scroll">
    <div v-if="badge" class="badge">{{ badge }}</div>
    <h2>{{ title }} <span class="text-accent">{{ accentTitle }}</span></h2>
    
    <!-- MOBILE TABS -->
    <div class="mobile-step-tabs">
      <div class="tabs-row">
        <button 
          v-for="(step, idx) in steps" 
          :key="idx" 
          :class="['tab-btn', { active: modelValue === idx }]"
          @click="$emit('update:modelValue', idx); $emit('step-click')"
        >
          {{ idx + 1 }}
        </button>
      </div>
      <div v-if="steps[modelValue]" class="tab-content-mobile">
        <h4>{{ steps[modelValue]!.title }}</h4>
        <p>{{ steps[modelValue]!.desc }}</p>
      </div>
    </div>

    <!-- DESKTOP LIST -->
    <div class="step-list desktop-only">
      <div 
        v-for="(step, idx) in steps" 
        :key="idx" 
        :class="['step-item', { active: modelValue === idx }]" 
        @click="$emit('update:modelValue', idx); $emit('step-click')"
      >
        <div class="step-number">{{ idx + 1 }}</div>
        <div class="step-text">
          <h4>{{ step.title }}</h4>
          <p>{{ step.desc }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.walkthrough-content h2 { font-size: clamp(2rem, 5vw, 3.5rem); font-weight: 800; margin-bottom: 40px; color: #fff; }
.badge { display: inline-flex; padding: 6px 14px; background: rgba(226, 27, 13, 0.1); color: #e21b0d; border-radius: 20px; font-weight: 800; font-size: 0.75rem; text-transform: uppercase; margin-bottom: 20px; }

/* Desktop Step List */
.step-list { display: flex; flex-direction: column; gap: 24px; }
.step-item { display: flex; gap: 20px; padding: 20px; border-radius: 20px; transition: all 0.3s ease; cursor: pointer; border: 1px solid transparent; }
.step-item.active { background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.1); }
.step-number { width: 32px; height: 32px; background: rgba(255, 255, 255, 0.1); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; flex-shrink: 0; color: #fff; }
.step-item.active .step-number { background: #e21b0d; color: white; }
.step-text h4 { font-size: 1.1rem; font-weight: 700; margin-bottom: 4px; color: #fff; }
.step-text p { font-size: 0.95rem; color: var(--text-secondary); line-height: 1.5; }

/* Mobile Tabs */
.mobile-step-tabs { display: none; flex-direction: column; gap: 16px; margin-bottom: 40px; align-items: center; text-align: center; scroll-margin-top: 80px; }
.tabs-row { display: flex; gap: 12px; margin-bottom: 4px; justify-content: center; }
.tab-btn { width: 32px; height: 32px; border-radius: 50%; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.1); color: #fff; font-weight: 800; cursor: pointer; transition: all 0.2s ease; font-size: 0.85rem; display: flex; align-items: center; justify-content: center; }
.tab-btn.active { background: #e21b0d; border-color: #e21b0d; box-shadow: 0 4px 12px rgba(226, 27, 13, 0.3); }
.tab-content-mobile { background: rgba(255,255,255,0.03); padding: 24px; border-radius: 24px; border: 1px solid rgba(255,255,255,0.05); height: 180px; width: 100%; box-sizing: border-box; display: flex; flex-direction: column; justify-content: center; flex: none; }
.tab-content-mobile h4 { font-size: 1.2rem; font-weight: 800; color: #fff; margin-bottom: 8px; }
.tab-content-mobile p { font-size: 1rem; color: var(--text-secondary); line-height: 1.5; margin: 0; }

@media (max-width: 768px) {
  .desktop-only { display: none; }
  .mobile-step-tabs { display: flex; }
}
</style>
